// src/pages/AppraisalWorkflowAdvanced.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * AppraisalWorkflowAdvanced.jsx
 * Enterprise-grade Appraisal Workflow Builder (Zoho + Keka hybrid)
 *
 * Features:
 * - Poppins + Roboto fonts
 * - Clean timeline chips + step cards
 * - Drag & drop reordering using HTML5 DnD
 * - Multi-approver support (parallel approvers)
 * - Conditional step expressions (string)
 * - Import / Export JSON
 * - Save to localStorage
 * - Preview & Validate workflow
 * - Compact editor modal (portal) that won't overlap app header
 *
 * Usage:
 * - Add import and route to your App.js:
 *   import AppraisalWorkflowAdvanced from './pages/AppraisalWorkflowAdvanced';
 *   <Route path="/admin/appraisal-workflow-advanced" element={<AppraisalWorkflowAdvanced />} />
 */

// LocalStorage key
const LS_KEY = "hrms_appraisal_workflow_advanced_v1";

// small UID helper
const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 999)}`;

// default roles for dropdown
const DEFAULT_ROLES = [
  "Employee",
  "Manager",
  "Reviewer",
  "HOD",
  "HR",
  "Panel",
  "Compensation Committee",
];

// default sample workflow
const DEFAULT_WORKFLOW = [
  {
    id: uid("step"),
    name: "Self Assessment",
    role: "Employee",
    parallelApprovers: [], // e.g. ["Manager A", "Manager B"]
    conditional: { enabled: false, expression: "" },
    optional: false,
    notes: "",
  },
  {
    id: uid("step"),
    name: "Manager Review",
    role: "Manager",
    parallelApprovers: [],
    conditional: { enabled: false, expression: "" },
    optional: false,
    notes: "",
  },
  {
    id: uid("step"),
    name: "HR Approval",
    role: "HR",
    parallelApprovers: [],
    conditional: { enabled: false, expression: "" },
    optional: false,
    notes: "",
  },
];

// Tiny UI components
const IconButton = ({ children, onClick, title }) => (
  <button
    onClick={onClick}
    title={title}
    className="p-1 rounded-md hover:bg-slate-100 transition text-slate-600"
  >
    {children}
  </button>
);

// Modal portal (compact — limited height so it doesn't cover header fully)
const PortalModal = ({ open, onClose, title, children, width = "max-w-3xl" }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
        style={{ background: "rgba(10,11,13,0.35)" }}
      >
        <motion.div
          initial={{ y: -10, scale: 0.99 }}
          animate={{ y: 0, scale: 1 }}
          exit={{ y: -10, scale: 0.99 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[70vh] overflow-auto`}
        >
          <div className="sticky top-0 bg-white z-10 border-b px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
            </div>
            <div>
              <button className="px-3 py-1 rounded-md text-slate-600 hover:bg-slate-100" onClick={onClose}>
                ✕
              </button>
            </div>
          </div>
          <div className="p-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default function AppraisalWorkflowAdvanced() {
  // fonts
  const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap');`;
  const fontStyle = { fontFamily: "Poppins, Roboto, sans-serif" };

  // state
  const [workflow, setWorkflow] = useState(DEFAULT_WORKFLOW);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorDraft, setEditorDraft] = useState(null);
  const [toast, setToast] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [filterRole, setFilterRole] = useState("");
  const fileInputRef = useRef(null);

  // load
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) setWorkflow(JSON.parse(raw));
    } catch (e) {
      console.warn("Failed to parse saved workflow", e);
    }
  }, []);

  // save
  const persist = (next) => {
    setWorkflow(next);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      setToast("Workflow saved");
      setTimeout(() => setToast(""), 1800);
    } catch (e) {
      console.error(e);
      setToast("Save failed");
      setTimeout(() => setToast(""), 1800);
    }
  };

  // add new step (opens editor)
  const handleAddStep = (afterIndex = null) => {
    const newStep = {
      id: uid("step"),
      name: "New Step",
      role: "Manager",
      parallelApprovers: [],
      conditional: { enabled: false, expression: "" },
      optional: false,
      notes: "",
    };
    const next = [...workflow];
    if (afterIndex === null) next.push(newStep);
    else next.splice(afterIndex + 1, 0, newStep);
    persist(next);
    setSelectedStepId(newStep.id);
    setEditorDraft(newStep);
    setIsEditorOpen(true);
  };

  // open editor for existing step
  const openEditor = (step) => {
    setEditorDraft(JSON.parse(JSON.stringify(step)));
    setIsEditorOpen(true);
    setSelectedStepId(step.id);
  };

  // save editor draft to workflow
  const saveEditor = () => {
    if (!editorDraft) return;
    const next = workflow.map((s) => (s.id === editorDraft.id ? editorDraft : s));
    persist(next);
    setIsEditorOpen(false);
    setEditorDraft(null);
  };

  // delete
  const deleteStep = (id) => {
    if (!window.confirm("Delete this step? This cannot be undone.")) return;
    const next = workflow.filter((s) => s.id !== id);
    persist(next);
    if (selectedStepId === id) {
      setSelectedStepId(null);
      setIsEditorOpen(false);
    }
  };

  // drag handlers (HTML5 DnD)
  const onDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(index));
    } catch {}
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = dragIndex !== null ? dragIndex : Number(e.dataTransfer.getData("text/plain"));
    if (sourceIndex === undefined || sourceIndex === null) return;
    if (sourceIndex === targetIndex) return setDragIndex(null);

    const next = [...workflow];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    persist(next);
    setDragIndex(null);
  };

  // reorder helper (keyboard friendly)
  const moveStep = (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= workflow.length) return;
    const next = [...workflow];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    persist(next);
  };

  // import / export
  const exportWorkflow = () => {
    const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: "application/json" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `workflow_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(href);
    setToast("Exported workflow JSON");
    setTimeout(() => setToast(""), 1400);
  };

  const importWorkflow = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!Array.isArray(parsed)) throw new Error("Invalid format");
        persist(parsed);
        setToast("Imported workflow");
        setTimeout(() => setToast(""), 1400);
      } catch (e) {
        console.error(e);
        setToast("Import failed: invalid JSON");
        setTimeout(() => setToast(""), 2000);
      }
    };
    reader.readAsText(file);
  };

  // preview / validation
  const validateWorkflow = (wf = workflow) => {
    const errs = [];
    // simple rules: first step can't be optional, names must be non-empty, no duplicate IDs
    wf.forEach((s, i) => {
      if (!s.name || s.name.trim() === "") errs.push(`Step ${i + 1} has empty name`);
      if (i === 0 && s.optional) errs.push("First step cannot be optional");
    });
    const ids = wf.map((s) => s.id);
    if (new Set(ids).size !== ids.length) errs.push("Duplicate step IDs found");
    return errs;
  };

  // UI derived
  const previewErrors = validateWorkflow();

  return (
    <div className="min-h-screen bg-slate-50 p-6" style={fontStyle}>
      <style>{fontImport}</style>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed top-6 right-6 z-50 bg-emerald-600 text-white px-4 py-2 rounded-md shadow"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Appraisal Workflow Builder</h1>
            <p className="text-sm text-slate-600 mt-1">
             
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Filter Role</label>
              <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-2 py-1 border rounded-md">
                <option value="">All</option>
                {DEFAULT_ROLES.map((r) => (
                  <option value={r} key={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => handleAddStep(null)} className="px-3 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 transition">
                + New Step
              </button>

              <label htmlFor="import" className="px-3 py-2 bg-white border rounded-lg cursor-pointer hover:bg-slate-50">
                Import
                <input id="import" ref={fileInputRef} onChange={(e) => importWorkflow(e.target.files?.[0])} type="file" accept=".json" hidden />
              </label>

              <button onClick={exportWorkflow} className="px-3 py-2 bg-white border rounded-lg hover:bg-slate-50">
                Export
              </button>

              <button onClick={() => persist(workflow)} className="px-3 py-2 bg-white border rounded-lg hover:bg-slate-50">
                Save
              </button>

              <button onClick={() => { setWorkflow(DEFAULT_WORKFLOW); persist(DEFAULT_WORKFLOW); }} className="px-3 py-2 bg-white border rounded-lg hover:bg-slate-50">
                Reset
              </button>
            </div>
          </div>
        </header>

        {/* Timeline chips */}
        <section className="bg-white p-4 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3 overflow-x-auto py-1 px-2">
            {workflow
              .filter((s) => (filterRole ? s.role === filterRole : true))
              .map((s, i) => (
                <div
                  key={s.id}
                  onClick={() => { setSelectedStepId(s.id); openEditor(s); }}
                  className="flex items-center gap-3 cursor-pointer p-2 rounded-full border hover:shadow-sm transition bg-gradient-to-br from-white to-slate-50"
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center font-semibold">
                    {i + 1}
                  </div>
                  <div className="min-w-[160px]">
                    <div className="text-sm font-semibold text-slate-800 truncate">{s.name}</div>
                    <div className="text-xs text-slate-500 truncate">{s.role}{s.optional ? " · Optional" : ""}</div>
                  </div>
                </div>
              ))}
          </div>
        </section>

        {/* Main builder area */}
        <main className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Steps list */}
          <section className="lg:col-span-2 space-y-4">
            <div className="bg-white p-4 rounded-2xl shadow">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-slate-800">Steps</h2>
                <div className="text-sm text-slate-500">{workflow.length} steps</div>
              </div>

              <div className="space-y-3">
                {workflow.map((s, i) => {
                  if (filterRole && s.role !== filterRole) return null;
                  return (
                    <motion.div
                      key={s.id}
                      drag
                      onDragStart={(e) => onDragStart(e, i)}
                      onDragOver={onDragOver}
                      onDrop={(e) => onDrop(e, i)}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ scale: 1.01 }}
                      className="bg-white border rounded-xl p-4 flex items-start gap-4 shadow-sm"
                    >
                      <div className="flex flex-col items-center w-12">
                        <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold">
                          {i + 1}
                        </div>
                        <div className="mt-2 flex flex-col gap-1">
                          <IconButton title="Move up" onClick={() => moveStep(i, -1)}>▲</IconButton>
                          <IconButton title="Move down" onClick={() => moveStep(i, 1)}>▼</IconButton>
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-base font-semibold text-slate-900">{s.name}</div>
                            <div className="text-xs text-slate-500">{s.role} {s.optional && <span className="ml-2 text-xs px-2 py-1 rounded bg-slate-100">Optional</span>}</div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button onClick={() => openEditor(s)} className="px-3 py-1 rounded-md border bg-white hover:bg-slate-50">Edit</button>
                            <button onClick={() => handleAddStep(i)} className="px-3 py-1 rounded-md bg-emerald-50 text-emerald-700 border">Add After</button>
                            <button onClick={() => deleteStep(s.id)} className="px-3 py-1 rounded-md bg-rose-50 text-rose-700 border">Delete</button>
                          </div>
                        </div>

                        {/* preview approvers */}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {s.parallelApprovers && s.parallelApprovers.length > 0 ? (
                            s.parallelApprovers.map((p, idx) => (
                              <div key={idx} className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full">{p}</div>
                            ))
                          ) : (
                            <div className="text-xs text-slate-400">No parallel approvers</div>
                          )}

                          {s.conditional.enabled && (
                            <div className="text-xs px-2 py-1 bg-yellow-50 text-yellow-800 rounded-full">
                              If: {s.conditional.expression || "—"}
                            </div>
                          )}
                        </div>

                        {s.notes && <div className="mt-3 text-sm text-slate-500">{s.notes}</div>}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Right: Preview / Validation / Quick actions */}
          <aside className="space-y-4">
            <div className="bg-white p-4 rounded-2xl shadow">
              <h3 className="text-md font-semibold text-slate-800">Quick Preview</h3>
              <p className="text-sm text-slate-600 mt-2">Flow preview and validation</p>

              <div className="mt-3 space-y-2">
                <div className="text-sm text-slate-700">Timeline</div>
                <div className="flex gap-2 flex-wrap mt-2">
                  {workflow.map((s, i) => (
                    <div key={s.id} className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-800">{i + 1}. {s.name}</div>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <h4 className="text-sm font-medium">Validation</h4>
                {previewErrors.length === 0 ? (
                  <div className="text-sm text-emerald-700 mt-2">No validation issues found</div>
                ) : (
                  <ul className="text-sm text-rose-600 mt-2 list-disc pl-5">
                    {previewErrors.map((e, idx) => <li key={idx}>{e}</li>)}
                  </ul>
                )}
              </div>

              <div className="mt-4 flex flex-col gap-2">
                <button onClick={() => setIsEditorOpen(true)} className="px-3 py-2 bg-indigo-600 text-white rounded-md">Open Selected Editor</button>
                <button onClick={() => { navigator.clipboard?.writeText(JSON.stringify(workflow, null, 2)); setToast("Copied JSON to clipboard"); setTimeout(()=>setToast(""),1400); }} className="px-3 py-2 border rounded-md">Copy JSON</button>
              </div>
            </div>

            <div className="bg-white p-4 rounded-2xl shadow">
              <h3 className="text-md font-semibold text-slate-800">Legend</h3>
              <div className="mt-2 text-sm text-slate-600 space-y-2">
                <div><span className="font-semibold">Parallel approvers</span> — multiple approvers can be assigned to a step (parallel).</div>
                <div><span className="font-semibold">Conditional</span> — step runs only if expression evaluates true (string expression; evaluation logic to be integrated server-side).</div>
              </div>
            </div>
          </aside>
        </main>
      </div>

      {/* Editor Modal */}
      <PortalModal open={isEditorOpen} onClose={() => { setIsEditorOpen(false); setEditorDraft(null); }} title={editorDraft ? `Edit: ${editorDraft.name}` : "Editor"}>
        {editorDraft ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-600">Step Name</label>
                <input
                  value={editorDraft.name}
                  onChange={(e) => setEditorDraft({ ...editorDraft, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div>
                <label className="text-xs text-slate-600">Primary Role</label>
                <select
                  value={editorDraft.role}
                  onChange={(e) => setEditorDraft({ ...editorDraft, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {DEFAULT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Notes (internal)</label>
                <textarea value={editorDraft.notes} onChange={(e) => setEditorDraft({ ...editorDraft, notes: e.target.value })} className="w-full px-3 py-2 border rounded-md h-20" />
              </div>
            </div>

            {/* Parallel approvers */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Parallel Approvers</h4>
                <div className="text-xs text-slate-500">Multiple approvers run in parallel</div>
              </div>

              <div className="mt-3 space-y-2">
                {(editorDraft.parallelApprovers || []).map((ap, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input className="px-2 py-1 border rounded-md flex-1" value={ap} onChange={(e) => {
                      const list = [...(editorDraft.parallelApprovers || [])];
                      list[idx] = e.target.value;
                      setEditorDraft({ ...editorDraft, parallelApprovers: list });
                    }} />
                    <button onClick={() => {
                      const list = [...(editorDraft.parallelApprovers || [])];
                      list.splice(idx, 1);
                      setEditorDraft({ ...editorDraft, parallelApprovers: list });
                    }} className="px-2 py-1 text-rose-600 rounded hover:bg-rose-50">Remove</button>
                  </div>
                ))}

                <div>
                  <button onClick={() => setEditorDraft({ ...editorDraft, parallelApprovers: [...(editorDraft.parallelApprovers || []), ""] })} className="px-3 py-1 bg-emerald-600 text-white rounded-md">+ Add Approver</button>
                </div>
              </div>
            </div>

            {/* Conditional */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Conditional Step</h4>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!editorDraft.conditional?.enabled}
                    onChange={(e) => setEditorDraft({ ...editorDraft, conditional: { ...(editorDraft.conditional || {}), enabled: e.target.checked } })}
                  />
                  Enabled
                </label>
              </div>

              {editorDraft.conditional?.enabled && (
                <div className="mt-3">
                  <label className="text-xs text-slate-600">Condition Expression</label>
                  <input className="w-full px-3 py-2 border rounded-md" value={editorDraft.conditional.expression || ""} onChange={(e) => setEditorDraft({ ...editorDraft, conditional: { ...(editorDraft.conditional || {}), expression: e.target.value } })} placeholder="e.g., tenure > 12 && manager_rating >= 4" />
                  <p className="text-xs text-slate-500 mt-1">Expression is stored as string; evaluate server-side or in a safe evaluator when applying.</p>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-slate-500">Step ID: <span className="font-mono text-xs">{editorDraft.id}</span></div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setIsEditorOpen(false); setEditorDraft(null); }} className="px-3 py-2 rounded-md bg-slate-100">Cancel</button>
                <button onClick={saveEditor} className="px-4 py-2 rounded-md bg-indigo-600 text-white">Save Step</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-slate-500 p-6">No step selected</div>
        )}
      </PortalModal>
    </div>
  );
}
